import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import ThreadList from "../components/chat/ThreadList";
import MessagePane from "../components/chat/MessagePane";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import { toast } from "sonner";

export default function Chat() {
  const { user } = useAuth();
  const {
    connected,
    threads,
    sendMessage,
    markRead,
    subscribe,
    setActiveOtherId,
    refreshThreads,
    testNotification,
  } = useChat();

  const [params, setParams] = useSearchParams();
  const initialOther = params.get("with");

  const [activeId, setActiveId] = useState(initialOther || null);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [contacts, setContacts] = useState([]);

  const loadMessages = useCallback(async (otherId) => {
    if (!otherId) return;
    try { const { data } = await api.get(`/messages/with/${otherId}`); setMessages(data); }
    catch (e) { logger.warn("loadMessages failed:", e?.message); }
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      if (user?.role === "counsellor" || user?.role === "admin") {
        const { data } = await api.get("/bookings");
        const map = new Map();
        for (const b of data) {
          const o = user.role === "counsellor" ? b.client : b.counsellor;
          if (o && !map.has(o.user_id)) map.set(o.user_id, o);
        }
        setContacts(Array.from(map.values()));
      } else {
        const { data } = await api.get("/counsellors");
        setContacts(data);
      }
    } catch (e) { logger.warn("loadContacts failed:", e?.message); }
  }, [user]);

  // Subscribe to incoming messages via global context
  useEffect(() => {
    const unsub = subscribe?.((m, readAck) => {
      if (m) {
        const me = user?.user_id;
        const otherId = m.sender_id === me ? m.recipient_id : m.sender_id;
        if (otherId === activeId) {
          setMessages((prev) => (prev.some((x) => x.message_id === m.message_id) ? prev : [...prev, m]));
        }
      } else if (readAck && readAck.thread_id) {
        setMessages((prev) => prev.map((mm) => (mm.thread_id === readAck.thread_id && mm.sender_id === user?.user_id ? { ...mm, read: true } : mm)));
      }
    });
    return () => unsub?.();
  }, [subscribe, activeId, user]);

  useEffect(() => { loadContacts(); }, [loadContacts]);
  useEffect(() => { setActiveOtherId?.(activeId); return () => { setActiveOtherId?.(null); }; }, [activeId, setActiveOtherId]);
  useEffect(() => {
    if (activeId) {
      loadMessages(activeId);
      markRead?.(activeId);
      refreshThreads?.();
    }
  }, [activeId, loadMessages, markRead, refreshThreads]);
  useEffect(() => {
    if (!activeId) { setActiveUser(null); return; }
    const t = threads.find((x) => x.other_id === activeId);
    if (t?.other) { setActiveUser(t.other); return; }
    const c = contacts.find((x) => x.user_id === activeId);
    if (c) setActiveUser(c);
  }, [activeId, threads, contacts]);

  const onSend = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body || !activeId) return;
    setText("");
    if (sendMessage(activeId, body)) return;
    try {
      await api.post("/messages", { recipient_id: activeId, body });
      await loadMessages(activeId);
      refreshThreads?.();
    } catch { toast.error("Could not send"); }
  };

  const startChatWith = (other) => {
    setActiveId(other.user_id);
    setActiveUser(other);
    setParams({ with: other.user_id });
    setSearchOpen(false);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div className="brand-card overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr]" style={{ minHeight: "70vh" }}>
          <ThreadList
            threads={threads}
            activeId={activeId}
            onSelect={startChatWith}
            connected={connected}
            onToggleSearch={() => setSearchOpen((v) => !v)}
            searchOpen={searchOpen}
            contacts={contacts}
            onPickContact={startChatWith}
            userRole={user?.role}
            onTestNotification={testNotification}
          />
          <section className="flex flex-col" style={{ minHeight: "70vh" }}>
            <MessagePane
              activeUser={activeId ? activeUser : null}
              messages={messages}
              myUserId={user?.user_id}
              text={text}
              setText={setText}
              onSend={onSend}
              connected={connected}
            />
          </section>
        </div>
      </main>
    </div>
  );
}
